import { getRoot, types } from 'mobx-state-tree'

import { FOCUSED_LOCATION_ZOOM } from '../constants'
import * as Analytics from '../utils/analytics'
import {
  applyLocationChipFilters,
  bookingExperiencePriority,
  getBoundingBox,
} from '../utils/helpers'
import { LocationModel } from './LocationModel'
import { LOCATION } from './queries'

export const LocationStore = types
  .model('LocationStore', {
    focusLocation: types.maybeNull(types.safeReference(LocationModel)),
    infoWindowOpen: true,
  })
  .views(self => ({
    get store() {
      return getRoot(self)
    },
    get locationsSortedByRank() {
      return Array.from(self.store.locations.values()).sort((a, b) => {
        if (a.highestProviderRank < b.highestProviderRank) {
          return -1
        }
        if (a.highestProviderRank > b.highestProviderRank) {
          return 1
        }
        if (self.store.searchStore.acceptsOnlineBookingFilterActive) {
          return (
            bookingExperiencePriority(a.bookingExperience) -
            bookingExperiencePriority(b.bookingExperience)
          )
        }
        return 0
      })
    },
    get claimedLocationsSortedByRank() {
      return self.locationsSortedByRank.filter(
        location =>
          (location.practice?.isClaimed ?? location.isClaimed) !== false
      )
    },
    get filterableLocationsSortedByRank() {
      if (self.store.showOnlyClaimedAccountsEnabled) {
        return self.claimedLocationsSortedByRank
      }
      return self.locationsSortedByRank
    },
    get searchMatchedLocationsBase() {
      const searchIds = new Set(self.store.searchStore.searchResultLocationIds)
      if (searchIds.size === 0) {
        return []
      }
      return self.filterableLocationsSortedByRank.filter(location =>
        searchIds.has(location.id)
      )
    },
    get searchMatchedLocations() {
      return applyLocationChipFilters(
        self.searchMatchedLocationsBase,
        self.store.searchStore,
        {
          acceptsOnlineBookingFilterActive:
            self.store.searchStore.acceptsOnlineBookingFilterActive,
        }
      )
    },
    /**
     * Locations that drive the results panel and map: bbox search hits when a
     * search has run, otherwise the full chip-filtered store (e.g. partner
     * load-all before the first search).
     */
    get activeSearchResultLocations() {
      if (self.store.searchStore.searchResultLocationIds.length > 0) {
        return self.searchMatchedLocations
      }
      return self.locations
    },
    get locations() {
      return applyLocationChipFilters(
        self.filterableLocationsSortedByRank,
        self.store.searchStore,
        {
          acceptsOnlineBookingFilterActive:
            self.store.searchStore.acceptsOnlineBookingFilterActive,
        }
      )
    },
    get hasLocations() {
      return self.activeSearchResultLocations.length > 0
    },
  }))
  .actions(self => ({
    setFocusLocation(location, { openInfoWindow = true } = {}) {
      Analytics.focusLocation(location.id, self.store.partnerId)
      self.infoWindowOpen = openInfoWindow
      self.focusLocation = location
      self.store.setMapCenter(
        getBoundingBox(
          location.latNumber,
          location.lngNumber,
          FOCUSED_LOCATION_ZOOM
        )
      )
    },
    resetFocusLocation() {
      self.focusLocation = null
      self.infoWindowOpen = false
    },
    maybeFetchLocation(locationId) {
      if (self.store.locations.has(locationId)) {
        self.store.debug(`NOT LOADING LOCATION WITH ID ${locationId}`)
      } else {
        self.store.debug(`LOADING LOCATION WITH ID ${locationId}`)
        self.store.query(LOCATION, { id: locationId })
      }
    },
    ensureLocationHydrated(locationId) {
      if (!locationId) {
        return Promise.resolve(undefined)
      }
      const existing = self.store.locations.get(locationId)
      if (
        existing?.street &&
        existing.lat !== null &&
        existing.lat !== undefined &&
        existing.lat !== ''
      ) {
        return Promise.resolve(existing)
      }
      self.store.debug(`HYDRATING LOCATION WITH ID ${locationId}`)
      return self.store.query(LOCATION, { id: locationId })
    },
  }))
